"use client";

import {
  Attachment,
  AttachmentPreview,
  Attachments,
} from "@/components/ai-elements/attachments";
import { GeneratedFileRow } from "@/components/generated-file-row";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { addGeneratedFileToProject, isFileInProject } from "@/lib/add-generated-file-to-project";
import { downloadFile } from "@/lib/download-file";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import {
  ArrowUpRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { ArtifactPart } from "@/types/chat";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement, useCallback, useEffect, useRef, useState } from "react";

import { CodeBlock } from "./code-block";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn("group not-prose mb-4 w-full rounded-md border", className)}
    {...props}
  />
);

export type ToolPart = ToolUIPart | DynamicToolUIPart;

/**
 * Anthropic and OpenAI web search are registered under different tool_names
 * (`web_search` / `web_search_openai`) so neither provider tries to validate the
 * other's stored results — see 20260824000001_namespace_openai_provider_tool_names.
 * Rendering does not care which produced a part: getWebSearchQuery and
 * getWebSearchResults below already read both output shapes.
 */
const WEB_SEARCH_TOOL_NAMES = ["web_search", "web_search_openai"] as const;

export const isWebSearchToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? (WEB_SEARCH_TOOL_NAMES as readonly string[]).includes(part.toolName)
    : (WEB_SEARCH_TOOL_NAMES as readonly string[]).includes(
        part.type.replace(/^tool-/, "")
      );

export const isMemoryToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "memory"
    : part.type === "tool-memory";

/**
 * The memory tool's execute() (src/lib/mem0.ts) returns a plain confirmation
 * or error string per command — there's no structured success flag, so we
 * match on the known literal prefixes it returns for each mutating command.
 * "view" is a read, not a change, so it doesn't surface a notice; any other
 * error/no-op string also returns null (no notice).
 */
export const getMemoryToolNotice = (
  input: unknown,
  output: unknown
): { message: string } | null => {
  if (!isRecord(input) || typeof output !== "string" || !output) return null;
  const command = input.command;

  if (command === "create" && output.startsWith("File created successfully")) {
    return { message: "Memory updated." };
  }
  if ((command === "str_replace" || command === "insert") && output.includes("has been edited")) {
    return { message: "Memory updated." };
  }
  if (command === "delete" && output.startsWith("Successfully deleted")) {
    return { message: "Memory removed." };
  }
  if (command === "rename" && output.startsWith("Successfully renamed")) {
    return { message: "Memory updated." };
  }
  return null;
};

/** True for any command that actually changes memory (not a "view" read). */
export const isMemoryMutationCommand = (input: unknown): boolean =>
  isRecord(input) && input.command !== "view";

/** The memory file path a command targets — "new_path" for rename, else "path". */
export const getMemoryToolPath = (input: unknown): string | null => {
  if (!isRecord(input)) return null;
  if (input.command === "rename" && typeof input.new_path === "string") return input.new_path;
  return typeof input.path === "string" ? input.path : null;
};

export const isWebFetchToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "web_fetch"
    : part.type === "tool-web_fetch";

export const isKnowledgeBaseToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "knowledge_base"
    : part.type === "tool-knowledge_base";

export const isMyFilesToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "my_files"
    : part.type === "tool-my_files";

export const getMyFilesAction = (input: unknown): "list" | "search" | null => {
  if (!isRecord(input)) return null;
  return input.action === "list" || input.action === "search" ? input.action : null;
};

export type MyFilesListedFile = { id: string; name: string };

export const getMyFilesListedFiles = (output: unknown): MyFilesListedFile[] => {
  if (!isRecord(output) || !Array.isArray(output.files)) return [];
  return (output.files as Array<Record<string, unknown>>).filter(
    (f): f is Record<string, unknown> & { id: string; name: string } =>
      isRecord(f) && typeof f.id === "string" && typeof f.name === "string"
  );
};

export const isPresentFilesTool = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "present_files"
    : part.type === "tool-present_files";

export const isCreateFileTool = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "create_file"
    : part.type === "tool-create_file";

export const isEditFileTool = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "edit_file"
    : part.type === "tool-edit_file";

export const getPresentFilesOutput = (
  output: unknown
): Array<{ filename: string; content: string }> | null => {
  if (!isRecord(output) || !Array.isArray(output.files)) return null;
  return output.files.filter(
    (f): f is { filename: string; content: string } =>
      isRecord(f) &&
      typeof f.filename === "string" &&
      typeof f.content === "string"
  );
};

export const getKnowledgeBaseQuery = (input: unknown): string | null => {
  if (!isRecord(input)) return null;
  if (typeof input.query === "string") return input.query;
  if (Array.isArray(input.queries) && typeof input.queries[0] === "string") {
    return input.queries[0];
  }
  return null;
};

export const getEditFileDiff = (output: unknown): string | null => {
  if (!isRecord(output) || typeof output.diff !== "string") return null;
  return output.diff || null;
};

const getEditFileError = (output: unknown): string | null => {
  if (!isRecord(output) || typeof output.error !== "string") return null;
  return output.error || null;
};

const getEditFileViewContent = (output: unknown): string | null => {
  // Only the view command returns { content: string } with no diff/filename fields
  if (!isRecord(output) || typeof output.content !== "string") return null;
  if ("diff" in output || "filename" in output || "error" in output) return null;
  return output.content || null;
};

const FileDiff = ({ diff }: { diff: string }) => {
  const lines = diff.split("\n");
  return (
    <div className="p-2 font-mono text-xs">
      {lines.map((line, i) => {
        const isAdd = line.startsWith("+");
        const isDel = line.startsWith("-");
        const isHdr = line.startsWith("@@");
        return (
          <div
            key={i}
            className={cn(
              "whitespace-pre-wrap break-all px-1 leading-5",
              isAdd && "bg-green-500/10 text-green-700 dark:text-green-400",
              isDel && "bg-red-500/10 text-red-700 dark:text-red-400",
              isHdr && "text-muted-foreground font-semibold",
              !isAdd && !isDel && !isHdr && "text-foreground/70",
            )}
          >
            {line || "\u00a0"}
          </div>
        );
      })}
    </div>
  );
};

export const getKnowledgeBaseDocumentName = (output: unknown): string | null => {
  if (!isRecord(output)) return null;
  const doc = output.document;
  if (!isRecord(doc) || typeof doc.name !== "string") return null;
  return doc.name || null;
};

export const getKnowledgeBaseAnswer = (output: unknown): string | null => {
  if (!isRecord(output) || typeof output.answer !== "string") return null;
  return output.answer.trim() || null;
};

export type KnowledgeBaseSource = {
  id: string | null;
  fileId: string | null;
  fileName: string | null;
  content: string;
  chunkOrder: number | null;
  similarity: number | null;
  rank: number | null;
};

export const getKnowledgeBaseSources = (output: unknown): KnowledgeBaseSource[] => {
  if (!isRecord(output) || !Array.isArray(output.sources)) return [];
  const fallbackName = getKnowledgeBaseDocumentName(output);
  return (output.sources as Array<Record<string, unknown>>)
    .filter((s): s is Record<string, unknown> => isRecord(s) && typeof s.content === "string")
    .map((s) => ({
      id: typeof s.id === "string" ? s.id : null,
      fileId: typeof s.fileId === "string" ? s.fileId : null,
      fileName: typeof s.fileName === "string" ? s.fileName : fallbackName,
      content: s.content as string,
      chunkOrder: typeof s.chunkOrder === "number" ? s.chunkOrder : null,
      similarity: typeof s.similarity === "number" ? s.similarity : null,
      rank: typeof s.rank === "number" ? s.rank : null,
    }));
};

export const isMeetingNotesToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "search_project_meeting_minutes" || part.toolName === "search_meeting_notes"
    : part.type === "tool-search_project_meeting_minutes" || part.type === "tool-search_meeting_notes";

export const getMeetingNotesQuery = (input: unknown): string | null => {
  if (!isRecord(input) || typeof input.query !== "string") return null;
  return input.query;
};

export const getMeetingNotesAnswer = (output: unknown): string | null => {
  if (!isRecord(output) || typeof output.answer !== "string") return null;
  return output.answer.trim() || null;
};

export type MeetingNotesSource = {
  id: string;
  recordingId: string;
  content: string;
  similarity: number;
};

export const getMeetingNotesSources = (output: unknown): MeetingNotesSource[] => {
  if (!isRecord(output) || !Array.isArray(output.sources)) return [];
  return (output.sources as MeetingNotesSource[]).filter(
    (s) => s && typeof s.content === "string"
  );
};

export const isSharedChatsToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "search_project_chats" || part.toolName === "search_shared_chats"
    : part.type === "tool-search_project_chats" || part.type === "tool-search_shared_chats";

export const getSharedChatsQuery = (input: unknown): string | null => {
  if (!isRecord(input) || typeof input.query !== "string") return null;
  return input.query;
};

export type SharedChatsSource = {
  id: string;
  chatId: string;
  content: string;
  similarity: number;
};

export const getSharedChatsSources = (output: unknown): SharedChatsSource[] => {
  if (!isRecord(output) || !Array.isArray(output.sources)) return [];
  return (output.sources as SharedChatsSource[]).filter(
    (s) => s && typeof s.content === "string"
  );
};

// Legacy tool-name aliases. This feature has been renamed twice — Data Store
// -> Note (WA-386) -> Register — and chat history is never rewritten, so
// stored message parts still carry whichever name was current when they were
// written. Model replay doesn't care (convertToModelMessages is name-agnostic),
// but the custom tool renderer below keys off the literal name, so every
// predicate here must keep matching all three generations indefinitely. Input
// and output readers do the same for the store_key/register_key and
// documents/notes field spellings.
//
// Watch the word "note": in the legacy *_project_note* names it meant a whole
// register; in today's *_register_note(s) names it means one record inside a
// register.
const toolNameMatches = (part: ToolPart, ...names: string[]) =>
  part.type === "dynamic-tool"
    ? names.includes(part.toolName)
    : names.some((n) => part.type === `tool-${n}`);
export const isCreateRegisterToolPart = (part: ToolPart) =>
  toolNameMatches(part, "create_register", "create_project_note", "create_project_data_store");

export const isDeleteRegisterToolPart = (part: ToolPart) =>
  toolNameMatches(part, "delete_register", "delete_project_note", "delete_project_data_store");

export const getCreateRegisterInput = (
  input: unknown
): { key: string | null; name: string | null } => {
  if (!isRecord(input)) return { key: null, name: null };
  return {
    key: typeof input.key === "string" ? input.key : null,
    name: typeof input.name === "string" ? input.name : null,
  };
};

export const getDeleteRegisterKey = (input: unknown): string | null => {
  if (!isRecord(input)) return null;
  if (typeof input.register_key === "string") return input.register_key;
  return typeof input.store_key === "string" ? input.store_key : null;
};

export const isAskClarifyingQuestionToolPart = (part: ToolPart) =>
  part.type === "dynamic-tool"
    ? part.toolName === "ask_clarifying_question"
    : part.type === "tool-ask_clarifying_question";

export type AskClarifyingQuestionOption = { label: string; description: string | null };

export const getAskClarifyingQuestionInput = (
  input: unknown
): { question: string | null; options: AskClarifyingQuestionOption[] } => {
  if (!isRecord(input)) return { question: null, options: [] };
  const options = Array.isArray(input.options)
    ? input.options
        .filter((o): o is Record<string, unknown> => isRecord(o) && typeof o.label === "string")
        .map((o) => ({
          label: o.label as string,
          description: typeof o.description === "string" ? o.description : null,
        }))
    : [];
  return {
    question: typeof input.question === "string" ? input.question : null,
    options,
  };
};

export const isUpsertRegisterNotesToolPart = (part: ToolPart) =>
  toolNameMatches(
    part,
    "upsert_register_notes",
    "upsert_project_note_document",
    "upsert_project_data_store_document"
  );

export type UpsertRegisterNoteInput = { externalKey: string; data: unknown };

/** `register_key`/`notes` today; `store_key`/`documents` in stored history. */
export const getRegisterKey = (input: unknown): string | null => {
  if (!isRecord(input)) return null;
  if (typeof input.register_key === "string") return input.register_key;
  return typeof input.store_key === "string" ? input.store_key : null;
};

export const getUpsertRegisterNotesInput = (
  input: unknown
): { registerKey: string | null; notes: UpsertRegisterNoteInput[] } => {
  if (!isRecord(input)) return { registerKey: null, notes: [] };
  const raw = Array.isArray(input.notes)
    ? input.notes
    : Array.isArray(input.documents)
      ? input.documents
      : [];
  const notes = raw
    .filter((n): n is Record<string, unknown> => isRecord(n) && typeof n.external_key === "string")
    .map((n) => ({ externalKey: n.external_key as string, data: n.data }));
  return { registerKey: getRegisterKey(input), notes };
};

export const isDeleteRegisterNotesToolPart = (part: ToolPart) =>
  toolNameMatches(
    part,
    "delete_register_notes",
    "delete_project_note_document",
    "delete_project_data_store_document"
  );

export const getDeleteRegisterNotesInput = (
  input: unknown
): { registerKey: string | null; externalKey: string | null; externalKeys: string[] } => {
  if (!isRecord(input)) return { registerKey: null, externalKey: null, externalKeys: [] };
  const externalKeys = Array.isArray(input.external_keys)
    ? input.external_keys.filter((key): key is string => typeof key === "string")
    : [];
  const externalKey = typeof input.external_key === "string" ? input.external_key : null;
  return {
    registerKey: getRegisterKey(input),
    externalKey,
    externalKeys: Array.from(new Set([...externalKeys, ...(externalKey ? [externalKey] : [])])),
  };
};

export type ToolWriteNotice = {
  kind: "memory" | "register";
  message: string;
  href?: string;
  actionLabel?: string;
};

const buildMemoryNoticeHref = (path: string) =>
  `/?nav=memories&memoryPath=${encodeURIComponent(path)}`;

const buildRegisterNoticeHref = ({
  projectId,
  registerKey,
  externalKey,
}: {
  projectId?: string | null;
  registerKey?: string | null;
  externalKey?: string | null;
}) => {
  if (!projectId || !registerKey) return null;
  const base = `/projects/${projectId}/registers/${encodeURIComponent(registerKey)}`;
  return externalKey ? `${base}?doc=${encodeURIComponent(externalKey)}` : base;
};

/**
 * Any tool call worth surfacing as a standalone toast above the composer —
 * not just the collapsed chain-of-thought trace — because it silently
 * changed something the user should know about. Add a new `kind` here (with
 * its own icon mapping in chat-view.tsx) rather than hand-rolling a parallel
 * "active<X>Notice" mechanism for the next tool that needs this.
 */
export const getToolWriteNotice = (
  part: ToolPart,
  { projectId }: { projectId?: string | null } = {}
): ToolWriteNotice | null => {
  if (part.state !== "output-available") return null;

  if (isMemoryToolPart(part)) {
    // A rename doesn't change what a memory says, only where it lives, so it
    // stays in the collapsed trace without earning a composer notice.
    if (isRecord(part.input) && part.input.command === "rename") return null;
    const notice = getMemoryToolNotice(part.input, part.output);
    const path = getMemoryToolPath(part.input);
    return notice
      ? {
          kind: "memory",
          message: notice.message,
          ...(path
            ? {
                href: buildMemoryNoticeHref(path),
                actionLabel: "Open memory",
              }
            : {}),
        }
      : null;
  }

  if (isUpsertRegisterNotesToolPart(part)) {
    const output = part.output;
    const written = isRecord(output)
      ? Array.isArray(output.notes)
        ? output.notes
        : Array.isArray(output.documents)
          ? output.documents
          : []
      : [];
    if (written.length > 0) {
      const { registerKey, notes } = getUpsertRegisterNotesInput(part.input);
      const externalKey = notes[0]?.externalKey ?? null;
      const href = buildRegisterNoticeHref({ projectId, registerKey, externalKey });
      return {
        kind: "register",
        message: "Register updated.",
        ...(href
          ? {
              href,
              actionLabel: "Open register",
            }
          : {}),
      };
    }
    return null;
  }

  if (isDeleteRegisterNotesToolPart(part)) {
    const output = part.output;
    if (isRecord(output) && output.deleted === true) {
      const { registerKey, externalKey } = getDeleteRegisterNotesInput(part.input);
      const href = buildRegisterNoticeHref({ projectId, registerKey, externalKey });
      return {
        kind: "register",
        message: "Note removed.",
        ...(href
          ? {
              href,
              actionLabel: "Open register",
            }
          : {}),
      };
    }
    return null;
  }

  // Reads (search/query) change nothing, so they never earn a notice — only
  // the writes above do.
  return null;
};

export const isListRegistersToolPart = (part: ToolPart) =>
  toolNameMatches(part, "list_registers", "list_project_notes", "list_project_data_stores");

export type RegisterSummary = {
  key: string;
  name: string;
  description: string | null;
  is_system?: boolean;
};

export const getListRegistersOutput = (output: unknown): RegisterSummary[] => {
  if (!isRecord(output)) return [];
  const rows = Array.isArray(output.registers)
    ? output.registers
    : Array.isArray(output.stores)
      ? output.stores
      : [];
  return (rows as RegisterSummary[]).filter(
    (r) => r && typeof r.key === "string" && typeof r.name === "string"
  );
};

export const isSearchRegisterNotesToolPart = (part: ToolPart) =>
  toolNameMatches(
    part,
    "search_register_notes",
    "search_project_note_documents",
    "search_project_data_store_documents"
  );

export const isQueryRegisterNotesToolPart = (part: ToolPart) =>
  toolNameMatches(
    part,
    "query_register_notes",
    "query_project_note_documents",
    "query_project_data_store_documents"
  );

export const getRegisterSearchInput = (
  input: unknown
): { registerKey: string | null; query: string | null } => {
  if (!isRecord(input)) return { registerKey: null, query: null };
  return {
    registerKey: getRegisterKey(input),
    query: typeof input.query === "string" ? input.query : null,
  };
};

export const getRegisterQueryInput = (
  input: unknown
): { registerKey: string | null; filter: Record<string, unknown> | null } => {
  if (!isRecord(input)) return { registerKey: null, filter: null };
  return {
    registerKey: getRegisterKey(input),
    filter: isRecord(input.filter) ? input.filter : null,
  };
};

export type RegisterNote = {
  id: string;
  external_key: string;
  data: unknown;
  similarity?: number;
};

export const getRegisterNotes = (output: unknown): RegisterNote[] => {
  if (!isRecord(output)) return [];
  const rows = Array.isArray(output.notes)
    ? output.notes
    : Array.isArray(output.documents)
      ? output.documents
      : [];
  return (rows as RegisterNote[]).filter((n) => n && typeof n.external_key === "string");
};

export const getRegisterAnswer = (output: unknown): string | null => {
  if (!isRecord(output) || typeof output.answer !== "string") return null;
  return output.answer.trim() || null;
};

export const getWebFetchUrl = (input: unknown): string | null => {
  if (!isRecord(input) || typeof input.url !== "string") return null;
  return input.url;
};

export const getWebFetchTitle = (output: unknown): string | null => {
  if (!isRecord(output)) return null;
  const content = output.content;
  if (!isRecord(content)) return null;
  const title = content.title;
  return typeof title === "string" ? title.trim() || null : null;
};

export const getWebSearchQuery = (input: unknown, output?: unknown): string | null => {
  if (isRecord(input) && typeof input.query === "string") return input.query;
  // OpenAI web search: query is in output.action.query, not in input
  if (isRecord(output) && isRecord(output.action) && typeof output.action.query === "string") {
    return output.action.query;
  }
  return null;
};

export const getWebFetchDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export type ToolHeaderProps = {
  title?: string;
  className?: string;
} & (
  | { type: ToolUIPart["type"]; state: ToolUIPart["state"]; toolName?: never }
  | {
      type: DynamicToolUIPart["type"];
      state: DynamicToolUIPart["state"];
      toolName: string;
    }
);

export type WebSearchResult = {
  url: string;
  title?: string | null;
  pageAge?: string | null;
  encryptedContent?: string;
  type?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isWebSearchResult = (value: unknown): value is WebSearchResult =>
  isRecord(value) &&
  typeof value.url === "string" &&
  (value.type === undefined || value.type === "web_search_result") &&
  (value.title === undefined || value.title === null || typeof value.title === "string") &&
  (value.encryptedContent === undefined || typeof value.encryptedContent === "string") &&
  (value.pageAge === undefined || value.pageAge === null || typeof value.pageAge === "string");

export const getWebSearchResults = (output: unknown): WebSearchResult[] | null => {
  if (Array.isArray(output) && output.length > 0 && output.every(isWebSearchResult)) {
    return output;
  }

  if (!isRecord(output)) {
    return null;
  }

  const results = output.results;

  if (Array.isArray(results) && results.length > 0 && results.every(isWebSearchResult)) {
    return results;
  }

  // OpenAI web search format: { action: {...}, sources: [{ type: "url", url: string }] }
  const sources = output.sources;
  if (Array.isArray(sources) && sources.length > 0) {
    const urlSources = sources
      .filter((s): s is { type: string; url: string } => isRecord(s) && s.type === "url" && typeof s.url === "string")
      .map((s) => ({ url: s.url }));
    if (urlSources.length > 0) return urlSources;
  }

  return null;
};

const getResultHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export const getResultTitle = (result: WebSearchResult) =>
  result.title?.trim() || getResultHostname(result.url);

const formatToolDisplayName = (name: string) =>
  name
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const WebSearchResults = ({ results }: { results: WebSearchResult[] }) => (
  <Attachments className="w-full" variant="list">
    {results.map((result, index) => {
      const hostname = getResultHostname(result.url);
      const title = getResultTitle(result);
      const attachmentData = {
        id: `${result.url}-${index}`,
        type: "source-document" as const,
        sourceId: result.url,
        mediaType: "text/html",
        title,
        filename: hostname,
      };

      return (
        <a
          aria-label={`Open ${title}`}
          className="block w-full rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          href={result.url}
          key={`${result.url}-${index}`}
          rel="noreferrer"
          target="_blank"
        >
          <Attachment className="w-full cursor-pointer transition-colors" data={attachmentData}>
            <AttachmentPreview />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-sm">{title}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-xs">
                <span className="truncate">{hostname}</span>
                {result.pageAge ? <span>{result.pageAge}</span> : null}
              </div>
            </div>
            <ArrowUpRightIcon className="size-4 shrink-0 text-muted-foreground" />
          </Attachment>
        </a>
      );
    })}
  </Attachments>
);

const statusLabels: Record<ToolPart["state"], string> = {
  "approval-requested": "Awaiting Approval",
  "approval-responded": "Responded",
  "input-available": "Running",
  "input-streaming": "Pending",
  "output-available": "Completed",
  "output-denied": "Denied",
  "output-error": "Error",
};

const statusIcons: Record<ToolPart["state"], ReactNode> = {
  "approval-requested": <ClockIcon className="size-4 text-yellow-600" />,
  "approval-responded": <CheckCircleIcon className="size-4 text-blue-600" />,
  "input-available": <ClockIcon className="size-4 animate-pulse" />,
  "input-streaming": <CircleIcon className="size-4" />,
  "output-available": <CheckCircleIcon className="size-4 text-green-600" />,
  "output-denied": <XCircleIcon className="size-4 text-orange-600" />,
  "output-error": <XCircleIcon className="size-4 text-red-600" />,
};

export const getStatusBadge = (status: ToolPart["state"]) => (
  <Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
    {statusIcons[status]}
    {statusLabels[status]}
  </Badge>
);

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  toolName,
  ...props
}: ToolHeaderProps) => {
  const derivedName =
    type === "dynamic-tool" ? toolName : type.split("-").slice(1).join("-");
  const displayName = title ?? formatToolDisplayName(derivedName);

  return (
    <CollapsibleTrigger
      className={cn(
        "flex w-full items-center justify-between gap-4 p-3",
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-2">
        <WrenchIcon className="size-4 text-muted-foreground" />
        <span className="font-medium text-sm">{displayName}</span>
        {getStatusBadge(state)}
      </div>
      <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 space-y-4 p-4 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => {
  if (isCodeExecutionInput(input)) {
    const lang = "command" in input ? "bash" : "python";
    return (
      <Collapsible className={cn("space-y-1.5", className)}>
        <CollapsibleTrigger className="group/script flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
          <span>Script</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="max-h-64 overflow-auto rounded-md bg-muted/50">
            <CodeBlock code={getCodeExecutionCode(input)} language={lang} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  if (isSkillCodeExecutionInput(input)) {
    const parts = [input.command, input.path].filter(Boolean);
    if (input.view_range) parts.push(`lines ${input.view_range[0]}–${input.view_range[1]}`);
    return (
      <div className={cn("flex items-center gap-1.5", className)}>
        <span className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          Executing skill {getSkillName(input) ?? "code"}
        </span>
      </div>
    );
  }

  const inputIsEmpty =
    input === null ||
    input === undefined ||
    (typeof input === "object" && !Array.isArray(input) && Object.keys(input as object).length === 0);

  if (inputIsEmpty) return null;

  return (
    <div className={cn("space-y-2 overflow-hidden", className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        Parameters
      </h4>
      <div className="rounded-md bg-muted/50">
        <CodeBlock code={JSON.stringify(input ?? null, null, 2)} language="json" />
      </div>
    </div>
  );
};

// ── ToolScript: combined Script collapsible (code + output) ─────────────────

export type ToolScriptProps = {
  input: ToolPart["input"];
  output: ToolPart["output"];
  errorText: ToolPart["errorText"];
  className?: string;
};

export const ToolScript = ({ input, output, errorText, className }: ToolScriptProps) => {
  if (!isCodeExecutionInput(input)) return null;

  const lang = "command" in input ? "bash" : "python";

  let outputNode: ReactNode = null;
  if (errorText) {
    outputNode = (
      <div className="rounded-md bg-destructive/10 p-2 text-destructive text-xs">
        {errorText}
      </div>
    );
  } else if (output !== undefined && output !== null) {
    const stdio = getCodeExecutionStdio(output);
    if (stdio) {
      const text = [stdio.stdout, stdio.stderr].filter(Boolean).join("\n");
      outputNode = <CodeBlock code={text} language="shell" />;
    } else if (typeof output === "object" && !isValidElement(output)) {
      outputNode = <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />;
    } else if (typeof output === "string") {
      outputNode = <CodeBlock code={output} language="json" />;
    }
  }

  return (
    <Collapsible className={cn("space-y-1.5", className)}>
      <CollapsibleTrigger className="group/script flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
        <span>Script</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="max-h-96 overflow-auto rounded-md bg-muted/50">
          <CodeBlock code={getCodeExecutionCode(input)} language={lang} />
          {outputNode && (
            <>
              <div className="px-4 py-1.5 font-medium text-muted-foreground text-xs uppercase tracking-wide border-t border-border">
                Output
              </div>
              <div className="max-h-64 overflow-auto">{outputNode}</div>
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ── Code execution file helpers ───────────────────────────────────────────────

type CodeExecFile = { fileId: string };

const isCodeExecutionResultType = (value: unknown) =>
  value === "bash_code_execution_result" ||
  value === "code_execution_result" ||
  value === "encrypted_code_execution_result";

const getCodeExecutionFilesFromContent = (content: unknown): CodeExecFile[] => {
  if (!Array.isArray(content)) {
    return [];
  }

  return content
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as Record<string, unknown>).file_id === "string"
    )
    .map((item) => ({ fileId: item.file_id as string }));
};

type CodeExecutionInput =
  | { type: "programmatic-tool-call"; code: string }
  | { type: "bash_code_execution"; command: string }
  | { code: string; containerId: string }; // OpenAI code interpreter

type SkillCodeExecutionInput = {
  type: "text_editor_code_execution";
  command: string;
  path?: string;
  view_range?: [number, number];
  text?: string;
  insert_line?: number;
};

export const isSkillCodeExecutionInput = (
  input: unknown
): input is SkillCodeExecutionInput => {
  if (typeof input !== "object" || input === null) return false;
  const data = input as Record<string, unknown>;
  return data.type === "text_editor_code_execution" && typeof data.command === "string";
};

export const getSkillName = (input: SkillCodeExecutionInput): string | null => {
  if (!input.path) return null;
  const segments = input.path.replace(/\\/g, "/").split("/").filter(Boolean);
  // Only trust a path that actually names a skill directory (".../skills/<name>/...").
  // There's no skill-name field on this content block (confirmed against the AI SDK's
  // Anthropic provider types) — a path like /tmp/woods_work2/report.docx or
  // /tmp/xyz/word/notes.txt has no relation to any skill, so guessing from its parent
  // folder mislabels arbitrary scratch directories as skill names (e.g. "Reading
  // Woods_work2 skill", "Executing skill word") and inflates the visible tool count
  // with fabricated skill-read narration.
  const skillsIdx = segments.indexOf("skills");
  if (skillsIdx !== -1 && segments[skillsIdx + 1]) return segments[skillsIdx + 1];
  return null;
};

export const isCodeExecutionInput = (
  input: unknown
): input is CodeExecutionInput => {
  if (typeof input !== "object" || input === null) return false;
  const data = input as Record<string, unknown>;
  return (
    (data.type === "programmatic-tool-call" && typeof data.code === "string") ||
    (data.type === "bash_code_execution" && typeof data.command === "string") ||
    (typeof data.code === "string" && typeof data.containerId === "string")
  );
};

const getCodeExecutionCode = (input: CodeExecutionInput): string =>
  "command" in input ? input.command : (input.code ?? "");

/** Returns the first descriptive comment from the code, or null.
 *  Skips sub-section markers like "(a) ...", "1.", "Step 1:", and short labels. */
export const getCodeExecutionDescription = (input: CodeExecutionInput): string | null => {
  // Prefer an explicit description field if the provider includes one
  const raw = input as Record<string, unknown>;
  if (typeof raw.description === "string" && raw.description.trim()) {
    return raw.description.trim();
  }

  const code = getCodeExecutionCode(input);
  for (const line of code.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("#")) continue;
    const desc = trimmed.replace(/^#+\s*/, "").trim();
    // Skip sub-section markers: "(a) ...", "(1) ...", "1.", "Step 2:", etc.
    if (/^\(?[a-zA-Z0-9]+[):.]\s/.test(desc)) continue;
    // Require at least 5 words for a meaningful sentence
    if (desc.split(/\s+/).length >= 5) return desc;
  }
  return null;
};

type CodeExecStdio = { stdout?: string; stderr?: string };

const getCodeExecutionStdio = (output: unknown): CodeExecStdio | null => {
  if (typeof output !== "object" || output === null) return null;
  const data = output as Record<string, unknown>;

  // OpenAI code interpreter: { outputs: [{ type: "logs", logs: string }] }
  if (Array.isArray(data.outputs)) {
    const logs = (data.outputs as unknown[])
      .filter((o): o is Record<string, unknown> => typeof o === "object" && o !== null)
      .filter((o) => o.type === "logs" && typeof o.logs === "string")
      .map((o) => (o.logs as string).trim())
      .filter(Boolean)
      .join("\n");
    if (logs) return { stdout: logs };
    return null;
  }

  let execData: Record<string, unknown> | null = null;

  if (isCodeExecutionResultType(data.type)) {
    execData = data;
  } else if (typeof data.result === "object" && data.result !== null) {
    const r = data.result as Record<string, unknown>;
    if (isCodeExecutionResultType(r.type)) execData = r;
  }

  if (!execData) return null;

  const stdout =
    typeof execData.stdout === "string" ? execData.stdout.trim() : "";
  const stderr =
    typeof execData.stderr === "string" ? execData.stderr.trim() : "";

  if (!stdout && !stderr) return null;

  return {
    stdout: stdout || undefined,
    stderr: stderr || undefined,
  };
};

/** Handles both bash_code_execution_result (bash commands) and
 *  code_execution_result (programmatic / skills-based execution). */
export function getCodeExecutionFiles(output: unknown): CodeExecFile[] {
  if (typeof output !== "object" || output === null) {
    return [];
  }

  const data = output as Record<string, unknown>;

  if (isCodeExecutionResultType(data.type)) {
    const files = getCodeExecutionFilesFromContent(data.content);
    if (files.length > 0) {
      return files;
    }
  }

  if (typeof data.result === "object" && data.result !== null) {
    const resultData = data.result as Record<string, unknown>;

    if (isCodeExecutionResultType(resultData.type)) {
      const files = getCodeExecutionFilesFromContent(resultData.content);
      if (files.length > 0) {
        return files;
      }
    }
  }

  return getCodeExecutionFilesFromContent(data.content);
}

// ── FileArtifact ──────────────────────────────────────────────────────────────

type FileArtifactState =
  | { status: "idle"; filename: string }
  | { status: "loading"; filename: string }
  | { status: "ready"; url: string; filename: string; file_id: string; file_path: string }
  | { status: "error"; filename: string };

type GeneratedFileProvider = "anthropic" | "openai";

const getExtensionFromFilename = (filename: string): string => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === filename.length - 1) return "file";
  return filename.slice(lastDot + 1).toLowerCase();
};

const getGeneratedFileQuery = ({
  provider,
  containerId,
  filename,
  inline = false,
}: {
  provider: GeneratedFileProvider;
  containerId?: string;
  filename?: string;
  inline?: boolean;
}) => {
  const params = new URLSearchParams();
  if (provider !== "anthropic") params.set("provider", provider);
  if (containerId) params.set("containerId", containerId);
  if (filename) params.set("filename", filename);
  if (inline) params.set("inline", "1");

  const query = params.toString();
  return query ? `?${query}` : "";
};

/** Live artifact — shown during/after streaming. Auto-saves to storage on mount. */
/** Resolve export filenames together before mounting auto-saving file cards. */
export function GeneratedFileArtifacts({
  fileIds,
  ...props
}: Omit<React.ComponentProps<typeof GeneratedFileArtifact>, "fileId" | "filename" | "provider" | "containerId"> & { fileIds: string[] }) {
  const key = JSON.stringify(fileIds);
  const [resolved, setResolved] = useState<{ key: string; files: { fileId: string; filename?: string }[] } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const ids = JSON.parse(key) as string[];
    void Promise.all(ids.map(async (fileId) => {
      try {
        const response = await fetch(`/api/files/${encodeURIComponent(fileId)}`);
        if (response.ok) {
          const metadata = await response.json() as { filename?: string };
          return { fileId, filename: metadata.filename };
        }
      } catch { /* Keep independently identifiable files when metadata is unavailable. */ }
      return { fileId, filename: undefined };
    })).then((files) => {
      const names = new Set<string>();
      const latest = files.reverse().filter((file) => {
        const identity = file.filename ?? file.fileId;
        if (names.has(identity)) return false;
        names.add(identity);
        return true;
      }).reverse();
      if (!cancelled) setResolved({ key, files: latest });
    });
    return () => { cancelled = true; };
  }, [key]);
  if (resolved?.key !== key) return null;
  return <>{resolved.files.map((file) => <GeneratedFileArtifact key={file.fileId} {...file} {...props} />)}</>;
}

export const GeneratedFileArtifact = ({
  fileId,
  provider = "anthropic",
  containerId,
  filename,
  chatId,
  projectId,
  onOpenInSidebar,
  onArtifactSaved,
}: {
  fileId: string;
  provider?: GeneratedFileProvider;
  containerId?: string;
  filename?: string;
  chatId?: string | null;
  /** Current project, if this chat belongs to one — enables "Add to project". */
  projectId?: string | null;
  onOpenInSidebar?: (
    filename: string,
    url: string,
    onAddToProject?: () => Promise<{ ok: boolean; error?: string }>
  ) => void;
  onArtifactSaved?: (artifact: ArtifactPart) => void;
}) => {
  const [state, setState] = useState<FileArtifactState>({
    status: "idle",
    filename: filename ?? "Generated document",
  });
  const [addToProjectState, setAddToProjectState] = useState<"idle" | "pending" | "added">("idle");
  const onArtifactSavedRef = useRef(onArtifactSaved);
  useEffect(() => { onArtifactSavedRef.current = onArtifactSaved; }, [onArtifactSaved]);

  // Reflect that this file was already attached to the project in a past
  // session — otherwise the button would incorrectly reset to "Add" on reload.
  useEffect(() => {
    if (state.status !== "ready" || !projectId) return;
    let cancelled = false;
    void isFileInProject({ fileId: state.file_id, projectId }).then((already) => {
      if (!cancelled && already) setAddToProjectState("added");
    });
    return () => { cancelled = true; };
  }, [state, projectId]);

  // Eagerly fetch just the filename so the loading row shows the real name
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(
          `/api/files/${encodeURIComponent(fileId)}${getGeneratedFileQuery({
            provider,
            containerId,
            filename,
          })}`
        );
        if (r.ok) {
          const meta = (await r.json()) as { filename?: string };
          if (meta.filename) {
            setState((prev) => prev.status !== "ready" ? { ...prev, filename: meta.filename! } : prev);
          }
        }
      } catch {
        // keep placeholder name
      }
    })();
  }, [fileId, provider, containerId, filename]);

  // Auto-save to Supabase storage on mount so the file is persisted and
  // ArtifactPart is written to message.parts without user interaction.
  const savedRef = useRef(false);
  useEffect(() => {
    if (!chatId || savedRef.current) return;
    savedRef.current = true;

    let cancelled = false;

    (async () => {
      setState((prev) => ({ ...prev, status: "loading" }));
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`/api/files/${encodeURIComponent(fileId)}/save`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ chatId, provider, containerId, filename }),
        });

        if (cancelled) return;

        if (res.ok) {
          const data = (await res.json()) as { url: string; filename: string; file_id: string; file_path: string };
          setState({ status: "ready", ...data });
          if (data.file_id) {
            onArtifactSavedRef.current?.({
              type: "artifact",
              artifact_type: getExtensionFromFilename(data.filename),
              file_id: data.file_id,
              name: data.filename,
              file_path: data.file_path,
            });
          }
        } else {
          console.warn("[FileArtifact] auto-save failed", res.status);
          setState((prev) => ({ ...prev, status: "error" }));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[FileArtifact] auto-save error", err);
          setState((prev) => ({ ...prev, status: "error" }));
        }
      }
    })();

    return () => { cancelled = true; };
  // fileId and chatId are stable for the lifetime of this artifact instance
  }, [fileId, provider, containerId, filename, chatId]);

  const resolveAndOpen = useCallback((action: "download" | "preview") => {
    if (state.status === "ready") {
      if (action === "preview") onOpenInSidebar?.(state.filename, state.url);
      else window.open(state.url, "_blank");
      return;
    }

    const url = `/api/files/${encodeURIComponent(fileId)}/content${getGeneratedFileQuery({
      provider,
      containerId,
      filename: state.filename,
      inline: action === "preview",
    })}`;
    if (action === "preview") onOpenInSidebar?.(state.filename, url);
    else window.open(url, "_blank");
  }, [state, fileId, provider, containerId, onOpenInSidebar]);

  const handleAddToProject = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (state.status !== "ready" || !projectId) return { ok: false, error: "Not ready" };
    setAddToProjectState("pending");
    const result = await addGeneratedFileToProject({
      fileId: state.file_id,
      filename: state.filename,
      projectId,
    });
    if (result.ok) {
      setAddToProjectState("added");
      toast.success("Added to project files.");
    } else {
      setAddToProjectState("idle");
      toast.error("Failed to add to project.");
    }
    return result;
  }, [state, projectId]);

  if (state.status === "ready") {
    return (
      <GeneratedFileRow
        filename={state.filename}
        url={state.url}
        onOpenInSidebar={
          onOpenInSidebar
            ? (fn, u) => onOpenInSidebar(fn, u, projectId ? handleAddToProject : undefined)
            : undefined
        }
        onAddToProject={projectId ? handleAddToProject : undefined}
        isAddingToProject={addToProjectState === "pending"}
        isAddedToProject={addToProjectState === "added"}
      />
    );
  }

  return (
    <GeneratedFileRow
      filename={state.filename}
      isLoading={state.status === "loading"}
      isError={state.status === "error"}
      onClickDownload={() => resolveAndOpen("download")}
      onClickPreview={() => resolveAndOpen("preview")}
    />
  );
};

// ── PersistedArtifact ─────────────────────────────────────────────────────────

/** Historical artifact loaded from DB — fetches signed URL lazily on first interaction. */
export const PersistedArtifact = ({
  artifact,
  projectId,
  onOpenInSidebar,
}: {
  artifact: ArtifactPart;
  /** Current project, if this chat belongs to one — enables "Add to project". */
  projectId?: string | null;
  onOpenInSidebar?: (
    filename: string,
    url: string,
    onAddToProject?: () => Promise<{ ok: boolean; error?: string }>
  ) => void;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [addToProjectState, setAddToProjectState] = useState<"idle" | "pending" | "added">("idle");

  // Reflect that this file was already attached to the project in a past
  // session — otherwise the button would incorrectly reset to "Add" on reload.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void isFileInProject({ fileId: artifact.file_id, projectId }).then((already) => {
      if (!cancelled && already) setAddToProjectState("added");
    });
    return () => { cancelled = true; };
  }, [artifact.file_id, projectId]);

  const resolveUrl = useCallback(async (): Promise<string | null> => {
    if (url) return url;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from("files")
        .createSignedUrl(artifact.file_path, 60 * 60);
      if (error || !data) throw error;
      setUrl(data.signedUrl);
      return data.signedUrl;
    } catch {
      setIsError(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [url, artifact.file_path]);

  const handleDownload = useCallback(async () => {
    const resolved = await resolveUrl();
    if (resolved) await downloadFile(resolved, artifact.name);
  }, [resolveUrl, artifact.name]);

  const handleAddToProject = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!projectId) return { ok: false, error: "No project" };
    setAddToProjectState("pending");
    const result = await addGeneratedFileToProject({
      fileId: artifact.file_id,
      filename: artifact.name,
      projectId,
    });
    if (result.ok) {
      setAddToProjectState("added");
      toast.success("Added to project files.");
    } else {
      setAddToProjectState("idle");
      toast.error("Failed to add to project.");
    }
    return result;
  }, [artifact.file_id, artifact.name, projectId]);

  const handlePreview = useCallback(async () => {
    const resolved = await resolveUrl();
    if (resolved) onOpenInSidebar?.(artifact.name, resolved, projectId ? handleAddToProject : undefined);
  }, [resolveUrl, artifact.name, onOpenInSidebar, projectId, handleAddToProject]);

  if (url) {
    return (
      <GeneratedFileRow
        filename={artifact.name}
        url={url}
        onOpenInSidebar={
          onOpenInSidebar
            ? (fn, u) => onOpenInSidebar(fn, u, projectId ? handleAddToProject : undefined)
            : undefined
        }
        onAddToProject={projectId ? handleAddToProject : undefined}
        isAddingToProject={addToProjectState === "pending"}
        isAddedToProject={addToProjectState === "added"}
      />
    );
  }

  return (
    <GeneratedFileRow
      filename={artifact.name}
      isLoading={isLoading}
      isError={isError}
      onClickDownload={handleDownload}
      onClickPreview={handlePreview}
      onAddToProject={projectId ? handleAddToProject : undefined}
      isAddingToProject={addToProjectState === "pending"}
      isAddedToProject={addToProjectState === "added"}
    />
  );
};

// ── ToolOutput ────────────────────────────────────────────────────────────────

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolPart["output"];
  errorText: ToolPart["errorText"];
  onOpenInSidebar?: (
    filename: string,
    url: string,
    onAddToProject?: () => Promise<{ ok: boolean; error?: string }>
  ) => void;
  onArtifactSaved?: (artifact: ArtifactPart) => void;
  hideLabel?: boolean;
  chatId?: string | null;
  /** Current project, if this chat belongs to one — enables "Add to project" on generated files. */
  projectId?: string | null;
  isEditFileTool?: boolean;
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  hideLabel = false,
  chatId,
  projectId,
  isEditFileTool = false,
  onOpenInSidebar,
  onArtifactSaved,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  // ── edit_file-specific rendering ──────────────────────────────────────────
  if (isEditFileTool) {
    const editError = getEditFileError(output);
    if (editError) {
      return (
        <div className={cn("rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive", className)} {...props}>
          {editError}
        </div>
      );
    }
    const viewContent = getEditFileViewContent(output);
    if (viewContent) {
      return (
        <Collapsible className={cn("space-y-1.5", className)} {...props}>
          <CollapsibleTrigger className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
            File contents
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="max-h-72 overflow-auto rounded-md bg-muted/50">
              <CodeBlock code={viewContent} language="html" />
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }
  }

  let Output = <div>{output as ReactNode}</div>;
  const webSearchResults = getWebSearchResults(output);
  const codeExecFiles = getCodeExecutionFiles(output);
  const editFileDiff = getEditFileDiff(output);
  if (webSearchResults) {
    Output = (
      <div className="p-2">
        <WebSearchResults results={webSearchResults} />
      </div>
    );
  } else if (codeExecFiles.length > 0) {
    Output = (
      <div className="flex w-full flex-col gap-2 px-3">
        {codeExecFiles.map(({ fileId }) => (
          <GeneratedFileArtifact
            key={fileId}
            fileId={fileId}
            chatId={chatId}
            projectId={projectId}
            onOpenInSidebar={onOpenInSidebar}
            onArtifactSaved={onArtifactSaved}
          />
        ))}
      </div>
    );
  } else {
    const stdio = getCodeExecutionStdio(output);
    if (stdio) {
      const text = [stdio.stdout, stdio.stderr].filter(Boolean).join("\n");
      Output = <CodeBlock code={text} language="shell" />;
    } else if (editFileDiff) {
      Output = <FileDiff diff={editFileDiff} />;
    } else if (typeof output === "object" && !isValidElement(output)) {
      Output = (
        <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />
      );
    } else if (typeof output === "string") {
      Output = <CodeBlock code={output} language="json" />;
    }
  }

  const shouldRenderInline = (hideLabel && !errorText) || (codeExecFiles.length > 0 && !errorText) || (!!editFileDiff && !errorText);

  if (shouldRenderInline) {
    return (
      <div className={className} {...props}>
        {Output}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)} {...props}>
      {!hideLabel ? (
        <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
          {errorText ? "Error" : "Result"}
        </h4>
      ) : null}
      <div
        className={cn(
          "overflow-x-auto rounded-md text-xs [&_table]:w-full",
          errorText
            ? "bg-destructive/10 text-destructive"
            : "bg-muted/50 text-foreground"
        )}
      >
        {errorText && <div>{errorText}</div>}
        {Output}
      </div>
    </div>
  );
};
